import unifold from "@unifold/core";
import globals from "@homer.core/globals";
import core from "@homer.core/api";
import factory from "@homer.core/factory";
import isObject from "@homer.utility/validator.isobject";
import isString from "@homer.utility/validator.isstring";
import querystringEncode from "@homer.utility/querystring.encode";
import jsonStringify from "@homer.task/json.stringify";
import clientInfo from "@homer.core/client.info";
import token from "./token.js";

// import packages that need to be loaded asap here
// @remark: recaptcha purposely not added here as it is fallback
import "@homer.challenge/turnstile";

const matchMethod = /^(?:POST|PUT|DELETE|PATCH)$/;
const flag = {
    prioritize: Math.pow(2, 1),
    isCacheable: Math.pow(2, 2),
    attachCredentials: Math.pow(2, 3),
    attachToken: Math.pow(2, 4),
    attachChallenge: Math.pow(2, 5),
    attachClienthints: Math.pow(2, 6),
    suppressEvents: Math.pow(2, 7),
};

export default class Request extends core.Request {
    constructor(method, data, header, flags = 0, client) {
        if (data && !isObject(data) && !isString(data)) {
            throw new TypeError("Argument `data` must be of type `Object|String`");
        }

        data = data || {};
        header = { "x-requested-with": "XMLHttpRequest", ...(header || {}) };

        const hasBody = matchMethod.test(method);

        super(method, data, {
            ...header,
            Accept: "application/json",
            "Content-Type": hasBody && data ? "application/json" : undefined,
        });

        this.header = Promise.resolve(this.header);
        this.data = Promise.resolve(this.data);
        this.flags = flags;

        this.header = this.header.then(async (header) => {
            const modifier = [];

            if (!!(flags & flag.attachToken)) {
                modifier.push(
                    token.get(client).then((token) => {
                        header["x-token"] = token || undefined;
                    }),
                );
            }

            if (!!(flags & flag.attachChallenge) && !(await clientInfo).isAutomated) {
                modifier.push(
                    globals.demand
                        .request("@homer.challenge/turnstile", "@homer.challenge/recaptcha")
                        .then(([turnstile, recaptcha]) => {
                            const challenges = { turnstile, recaptcha };

                            return Object.entries(challenges).reduce((chain, [id, challenge]) => {
                                return chain.catch(() => {
                                    new unifold.Event(`captcha/${id}/challenged`).dispatch();

                                    return challenge
                                        .get()
                                        .then((challenge) => {
                                            new unifold.Event(`captcha/${id}/success`).dispatch();
                                            header[`x-challenge-${id}`] = challenge;
                                        })
                                        .catch((reason) => {
                                            header[`x-challenge-${id}`] = `challenge failed (reason: ${reason || "unknown"})`;

                                            new unifold.Event(`captcha/${id}/fail`).dispatch();

                                            void globals.demand.request("@apfelkiste/errorhandler").then(([errorhandler]) => {
                                                errorhandler.handleMessage(`[@homer/api.homer:debug] ${id} challenge error`, {
                                                    logData: header[`x-challenge-${id}`],
                                                });
                                            });

                                            // important here
                                            throw new Error(reason);
                                        });
                                });
                            }, Promise.reject());
                        }),
                );
            }

            if (!!(flags & flag.attachClienthints)) {
                modifier.push(
                    globals.demand.request("@homer.core/client.hints").then(async ([clientHints]) => {
                        if (clientHints) {
                            header["x-client-hints"] = await jsonStringify.execute(clientHints);
                        }
                    }),
                );
            }

            return Promise.all(modifier)
                .then(() => header)
                .catch((error) => {
                    void factory.get("@schema/webpageelement.dialog.notification", {
                        type: "error",
                    });
                    throw error;
                });
        });

        this.data = this.data.then((data) => {
            if (data && !isString(data)) {
                return hasBody ? jsonStringify.execute(data) : querystringEncode(data);
            }

            return data;
        });
    }

    clone(client) {
        return Promise.all([this.header, this.data]).then(([header, data]) => {
            return new Request(this.method, data, { ...header }, this.flags, client);
        });
    }

    static flag = Object.freeze(flag);
}
